import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  Paper,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { 
  Search as SearchIcon, 
  FileDownload as FileDownloadIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  AccessTime as AccessTimeIcon,
  HelpOutline as HelpOutlineIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import { getClientPoints, ClientPoint, reconcilePoints } from '../services/clientService';
import { getProcesses, Process } from '../services/processService';
import { useAuth } from '../contexts/AuthContext';

export default function ClientPointsPage() {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [points, setPoints] = useState<ClientPoint[]>([]);
  const [filteredPoints, setFilteredPoints] = useState<ClientPoint[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('pendiente');
  const [activeProcessesCount, setActiveProcessesCount] = useState<number>(0);
  const [hasPendingPoints, setHasPendingPoints] = useState<boolean>(false);
  const theme = useTheme();
  const { userProfile } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pointsData, processesData] = await Promise.all([
          getClientPoints(),
          getProcesses()
        ]);
        
        setPoints(pointsData);
        setFilteredPoints(pointsData);
        
        // Verificar si hay puntos pendientes
        const pendingPointsExist = pointsData.some(point => point.status === 'pendiente');
        setHasPendingPoints(pendingPointsExist);
        
        // Contar procesos activos
        const activeProcesses = processesData.filter(process => process.status === 'active');
        setActiveProcessesCount(activeProcesses.length);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let result = [...points];
    
    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(
        (point) =>
          (point.client_name?.toLowerCase() || '').includes(searchLower) ||
          (point.consumer_name?.toLowerCase() || '').includes(searchLower) ||
          (point.promotion?.toLowerCase() || '').includes(searchLower) ||
          (point.status?.toLowerCase() || '').includes(searchLower) ||
          (point.cliend_id?.toString() || '').includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== 'todos') {
      result = result.filter((point) => point.status === statusFilter);
    }

    setFilteredPoints(result);
  }, [searchText, statusFilter, points]);

  const handleReconcileAndExport = async () => {
    try {
      setLoading(true);
      const result = await reconcilePoints();
      
      if (result.success) {
        // Recargar los datos después de la conciliación
        const [pointsData, processesData] = await Promise.all([
          getClientPoints(),
          getProcesses()
        ]);
        
        setPoints(pointsData);
        setFilteredPoints(pointsData);
        
        // Verificar si hay puntos pendientes
        const pendingPointsExist = pointsData.some(point => point.status === 'pendiente');
        setHasPendingPoints(pendingPointsExist);
        
        // Contar procesos activos
        const activeProcesses = processesData.filter(process => process.status === 'active');
        setActiveProcessesCount(activeProcesses.length);
        
        // Mostrar toast de éxito
        enqueueSnackbar('Conciliación ejecutada correctamente', { variant: 'success' });
      } else {
        // Mostrar el mensaje de error que viene en res
        enqueueSnackbar(result.res || 'Error al ejecutar la conciliación', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error al ejecutar procesos de conciliación:', error);
      enqueueSnackbar('Ocurrió un error al intentar ejecutar los procesos de conciliación', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (data: ClientPoint[]) => {
    const dataToExport = data.map((point: ClientPoint) => ({
      'Fecha': point.created_at,
      'ID FEMSA': point.cliend_id,
      'Cliente': point.client_name,
      'Consumidor': point.consumer_name,
      'Promoción': point.promotion,
      'Puntos': point.points,
      'Estado': point.status,
      'Fecha Conciliación': point.conciliation_date || 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Puntos Clientes');
    XLSX.writeFile(workbook, 'puntos_clientes_conciliados.xlsx');
  };
  
  // Mantener handleExportToExcel para compatibilidad
  const handleExportToExcel = () => {
    exportToExcel(filteredPoints);
  };

  const formatDate = (value: string) => {
    if (!value || value === '') return 'Sin definir';
    
    // Si la fecha viene en formato 'dd/MM/yy HH:mm', la convertimos a 'dd/MM/yyyy HH:mm'
    const shortYearMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2})$/);
    if (shortYearMatch) {
      const [_, day, month, year, hours, minutes] = shortYearMatch;
      const fullYear = parseInt(year) < 30 ? `20${year}` : `19${year}`; // Asume años 20xx para años < 30
      return `${day}/${month}/${fullYear} ${hours}:${minutes}`;
    }
    
    // Si la fecha ya viene en formato 'dd/MM/yyyy HH:mm', la devolvemos tal cual
    if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}(?::\d{2})?$/.test(value)) {
      return value;
    }
    
    // Si por alguna razón el formato es diferente, lo intentamos formatear
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return 'Formato inválido';
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Error';
    }
  };

  const columns: GridColDef<ClientPoint>[] = [
    { 
      field: 'created_at', 
      headerName: 'FECHA', 
      flex: 1,
      minWidth: 180,
      headerClassName: 'header-bold',
      valueFormatter: (value) => formatDate(value),
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : 'pendiente'}
        </Typography>
      ),
    },
    { 
      field: 'cliend_id', 
      headerName: 'ID FEMSA', 
      flex: 0.8,
      minWidth: 120,
      headerClassName: 'header-bold',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'client_name', 
      headerName: 'CLIENTE', 
      flex: 1.5,
      minWidth: 200,
      headerClassName: 'header-bold',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'consumer_name', 
      headerName: 'CONSUMIDOR', 
      flex: 1.5,
      minWidth: 200,
      headerClassName: 'header-bold',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'promotion', 
      headerName: 'PROMOCIÓN', 
      flex: 1.5,
      minWidth: 200,
      headerClassName: 'header-bold',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    { 
      field: 'points', 
      headerName: 'PUNTOS', 
      type: 'number',
      flex: 0.8,
      minWidth: 100,
      headerClassName: 'header-bold',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box 
          sx={{ 
            width: '100%', 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value ? params.value.toLocaleString('es-ES') : '0'}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'status', 
      headerName: 'ESTADO', 
      flex: 1,
      minWidth: 130,
      headerClassName: 'header-bold',
      renderCell: (params) => {
        const status = params.value as string;
        const statusConfig = {
          'conciliado': {
            bgcolor: theme.palette.success.main,
            color: theme.palette.common.white,
            borderColor: theme.palette.success.main,
            label: 'CONCILIADO',
            icon: <CheckCircleOutlineIcon sx={{ 
              fontSize: '0.8rem', 
              mr: 0.5, 
              verticalAlign: 'middle',
              color: theme.palette.common.white 
            }} />,
          },
          'pendiente': {
            bgcolor: theme.palette.common.white,
            color: theme.palette.common.black,
            borderColor: theme.palette.grey[400],
            label: 'PENDIENTE',
            icon: <AccessTimeIcon sx={{ 
              fontSize: '0.8rem', 
              mr: 0.5, 
              verticalAlign: 'middle',
              color: theme.palette.common.black 
            }} />,
          },
          default: {
            bgcolor: theme.palette.common.white,
            color: theme.palette.common.black,
            borderColor: theme.palette.grey[400],
            label: status?.toUpperCase() || 'SIN ESTADO',
            icon: <HelpOutlineIcon sx={{ 
              fontSize: '0.8rem', 
              mr: 0.5, 
              verticalAlign: 'middle',
              color: theme.palette.common.black 
            }} />,
          },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.default;

        return (
          <Box
            sx={{
              backgroundColor: config.bgcolor,
              color: config.color,
              border: `1px solid ${config.borderColor}`,
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '0.7rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '100px',
              height: '26px',
              boxSizing: 'border-box',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                opacity: 0.9,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              },
            }}
          >
            {config.icon}
            {config.label}
          </Box>
        );
      },
    },
    { 
      field: 'conciliation_date', 
      headerName: 'FECHA CONCILIACIÓN', 
      flex: 1,
      minWidth: 180,
      headerClassName: 'header-bold',
      valueFormatter: (params: { value: string }) => params.value ? formatDate(params.value) : '',
      renderCell: (params) => (
        <Typography variant="body2" color="text.primary">
          {params.value ? formatDate(params.value) : ''}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600, 
              color: '#C8102E',
              fontSize: '28px',
              lineHeight: 1.2,
              letterSpacing: '0.0075em',
              mb: 0,
              padding: '8px 0',
              fontFamily: 'Roboto, Arial, sans-serif'
            }}
          >
            Puntos Clientes
          </Typography>
          
          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '12px',
                color: '#C8102E',
                fontWeight: 500,
                display: 'block',
                mb: 0.5
              }}
            >
              {activeProcessesCount > 0 
                ? `Hay ${activeProcessesCount} proceso${activeProcessesCount === 1 ? '' : 's'} activo${activeProcessesCount === 1 ? '' : 's'} de conciliación automática`
                : 'No hay procesos activos de conciliación automática'
              }
            </Typography>
            {userProfile === 'Root' && (
              <Box
                component={Link}
                to="/configuracion/procesos"
                sx={{
                  textDecoration: 'none',
                  fontSize: '11px',
                  color: '#C8102E',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                <SettingsIcon sx={{ fontSize: '12px' }} />
                Ver procesos
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              variant="outlined"
              placeholder="Buscar por cliente, consumidor o promoción..."
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                minWidth: 300, 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
            
            <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="status-filter-label">Filtrar por estado</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as string)}
                label="Filtrar por estado"
              >
                <MenuItem value="todos">Todos los estados</MenuItem>
                <MenuItem value="pendiente">pendiente</MenuItem>
                <MenuItem value="conciliado">conciliado</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Forzar ejecución de procesos activos de conciliación">
              <Button
                variant="outlined"
                onClick={handleReconcileAndExport}
                startIcon={<CheckCircleOutlineIcon />}
                disabled={loading || !hasPendingPoints}
                title={!hasPendingPoints ? 'No hay puntos pendientes para conciliar' : ''}
                sx={{ 
                  color: '#C8102E',
                  backgroundColor: 'white',
                  borderColor: '#C8102E',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#C8102E',
                    color: 'white',
                    borderColor: '#C8102E',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(200, 16, 46, 0.1)',
                    color: 'rgba(200, 16, 46, 0.5)',
                    borderColor: 'rgba(200, 16, 46, 0.3)',
                  },
                  minWidth: '150px',
                  height: '40px',
                  borderRadius: '4px',
                }}
              >
                {loading ? 'Ejecutando procesos...' : 'Forzar Procesos'}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Paper sx={{ height: 'calc(100vh - 250px)', width: '100%', mt: 2, p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 'calc(100vh - 280px)', width: '100%' }}>
              <DataGrid
                rows={filteredPoints}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 20 },
                  },
                  sorting: {
                    sortModel: [{ field: 'created_at', sort: 'desc' }],
                  },
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                disableRowSelectionOnClick
                getRowId={(row) => `${row.cliend_id}-${row.created_at}-${row.promotion}`}
                getRowHeight={() => 'auto'}
                slots={{
                  toolbar: GridToolbar,
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                  '& .MuiDataGrid-columnHeader': {
                    padding: '8px 16px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    padding: '4px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&.MuiDataGrid-cell--withRenderer': {
                      padding: '4px 8px',
                    },
                  },
                  '& .MuiDataGrid-toolbarContainer': {
                    padding: theme.spacing(1, 2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '& .MuiButton-text': {
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                  '& .MuiTablePagination-root': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                  },
                }}
                density="comfortable"
                disableColumnMenu={false}
                localeText={{
                  toolbarDensity: 'Densidad',
                  toolbarDensityLabel: 'Densidad',
                  toolbarDensityCompact: 'Compacta',
                  toolbarDensityStandard: 'Estándar',
                toolbarDensityComfortable: 'Cómoda',
                toolbarFilters: 'Filtros',
                toolbarFiltersLabel: 'Mostrar filtros',
                toolbarFiltersTooltipHide: 'Ocultar filtros',
                toolbarFiltersTooltipShow: 'Mostrar filtros',
                toolbarExport: 'Exportar',
                toolbarExportLabel: 'Exportar',
                toolbarExportCSV: 'Descargar como CSV',
                toolbarExportPrint: 'Imprimir',
                filterPanelAddFilter: 'Agregar filtro',
                filterPanelDeleteIconLabel: 'Eliminar',
                filterPanelOperator: 'Operador',
                filterPanelOperatorAnd: 'Y',
                filterPanelOperatorOr: 'O',
                filterPanelColumns: 'Columnas',
                filterPanelInputLabel: 'Valor',
                filterPanelInputPlaceholder: 'Valor del filtro',
                filterOperatorContains: 'contiene',
                filterOperatorEquals: 'es igual a',
                filterOperatorStartsWith: 'comienza con',
                filterOperatorEndsWith: 'termina con',
                filterOperatorIs: 'es',
                filterOperatorNot: 'no es',
                filterOperatorAfter: 'es posterior a',
                filterOperatorOnOrAfter: 'es en o posterior a',
                filterOperatorBefore: 'es anterior a',
                filterOperatorOnOrBefore: 'es en o anterior a',
                filterOperatorIsEmpty: 'está vacío',
                filterOperatorIsNotEmpty: 'no está vacío',
                filterOperatorIsAnyOf: 'es cualquiera de',
                filterValueAny: 'cualquiera',
                filterValueTrue: 'verdadero',
                filterValueFalse: 'falso',
                columnHeaderSortIconLabel: 'Ordenar',
                columnMenuLabel: 'Menú',
                columnMenuShowColumns: 'Mostrar columnas',
                columnMenuFilter: 'Filtrar',
                columnMenuHideColumn: 'Ocultar',
                columnMenuUnsort: 'Desordenar',
                columnMenuSortAsc: 'Ordenar ASC',
                columnMenuSortDesc: 'Ordenar DESC',
                // Column panel text - Removed unsupported properties
                noRowsLabel: 'No hay datos para mostrar',
                noResultsOverlayLabel: 'No se encontraron resultados.'
              }}
            />
            </Box>
          )}
        </Paper>
      </Paper>
    </Box>
  );
}
